import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppLayout from '@cloudscape-design/components/app-layout';
import SideNavigation, { SideNavigationProps } from '@cloudscape-design/components/side-navigation';
import TopNavigation from '@cloudscape-design/components/top-navigation';
import { useAuth } from '../context/AuthContext';

/**
 * Props for the AppLayoutWrapper component
 */
export interface AppLayoutWrapperProps {
  /** The main content to render within the layout */
  children: React.ReactNode;
  /** Optional title for the content header */
  contentHeader?: React.ReactNode;
}

/**
 * AppLayoutWrapper component that provides consistent navigation layout.
 *
 * This component wraps the Cloudscape AppLayout and provides:
 * - Side navigation with links to all pages
 * - Admin section visibility based on user role
 * - Logout button in the header via TopNavigation
 * - Consistent layout across all pages
 *
 * @example
 * ```tsx
 * <AppLayoutWrapper>
 *   <DashboardContent />
 * </AppLayoutWrapper>
 * ```
 *
 * @param props - Component props
 * @returns The wrapped layout with navigation
 *
 * @remarks
 * Validates: Requirements 13.6 - The application shall provide a consistent navigation layout with side navigation
 */
export function AppLayoutWrapper({ children, contentHeader }: AppLayoutWrapperProps): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, logout } = useAuth();
  const [navigationOpen, setNavigationOpen] = useState(true);

  /**
   * Handle navigation item click
   * Prevents default link behavior and uses React Router navigation
   */
  const handleNavigationFollow = (event: CustomEvent<SideNavigationProps.FollowDetail>): void => {
    event.preventDefault();
    if (event.detail.href) {
      navigate(event.detail.href);
    }
  };

  /**
   * Handle logout action from top navigation
   */
  const handleLogout = async (): Promise<void> => {
    await logout();
    navigate('/login', { replace: true });
  };

  /**
   * Build navigation items based on user role.
   * Uses multi-level navigation for training programs to support
   * future expansion beyond Business Acumen.
   */
  const buildNavigationItems = (): SideNavigationProps.Item[] => {
    const items: SideNavigationProps.Item[] = [
      {
        type: 'link',
        text: 'Home',
        href: '/home',
      },
      {
        type: 'link',
        text: 'My Progress',
        href: '/my-progress',
      },
      { type: 'divider' },
      {
        type: 'section',
        text: 'Training Programs',
        items: [
          {
            type: 'link',
            text: 'Business Acumen for SA',
            href: '/programs/business-acumen',
          },
        ],
      },
    ];

    // Add admin section only if user has admin role
    if (isAdmin) {
      items.push(
        { type: 'divider' },
        {
          type: 'section',
          text: 'Administration',
          items: [
            {
              type: 'link',
              text: 'Evaluation Reviews',
              href: '/admin/evaluation-reviews',
            },
            {
              type: 'link',
              text: 'User Management',
              href: '/admin/users',
            },
            {
              type: 'link',
              text: 'Scenario Management',
              href: '/admin/scenarios',
            },
          ],
        }
      );
    }

    return items;
  };

  /**
   * Top navigation component with user info and logout
   */
  const topNavigation = (
    <TopNavigation
      identity={{
        href: '/home',
        title: 'GenAI Role Play Training',
        onFollow: (event) => {
          event.preventDefault();
          navigate('/home');
        },
      }}
      utilities={[
        {
          type: 'menu-dropdown',
          text: user?.name || user?.email || 'User',
          description: user?.email,
          iconName: 'user-profile',
          items: [
            {
              id: 'profile',
              text: 'Profile',
              disabled: true,
            },
            {
              id: 'logout',
              text: 'Sign out',
            },
          ],
          onItemClick: (event) => {
            if (event.detail.id === 'logout') {
              handleLogout();
            }
          },
        },
      ]}
    />
  );

  /**
   * Side navigation component with role-based links
   */
  const sideNavigation = (
    <SideNavigation
      activeHref={location.pathname}
      header={{
        href: '/home',
        text: 'Training Platform',
      }}
      onFollow={handleNavigationFollow}
      items={buildNavigationItems()}
    />
  );

  return (
    <>
      {topNavigation}
      <AppLayout
        navigation={sideNavigation}
        navigationOpen={navigationOpen}
        onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
        content={children}
        contentHeader={contentHeader}
        toolsHide={true}
        headerSelector="#top-navigation"
      />
    </>
  );
}

export default AppLayoutWrapper;
